import { Box, Button, Divider, List } from '@mui/material';
import { useCallback, useRef } from 'react';
import Typography from '@mui/material/Typography';
import { useNavigate, useParams } from 'react-router';
import { FormattedMessage } from 'react-intl';
import {
  FeaturedResourcesItem,
  ResourcesCarousel,
  ResourcesHeader,
  LoadingIndicator,
  useIntersectionObserver,
  FunctionBuilder,
  Routes,
  getImageUrl,
} from '@xolv-mybrightlink/common';
import { useEventSlaStatus } from '../hooks/useEventSla';
import { EventSlaPage } from './EventSlaPage';
import { useFetchEvents } from '../hooks/useFetchEvents';
import { useBookmarkEvent } from '../hooks/useBookmarkEvent';
import { useEventCategories } from '../hooks/useEventCategories';
import { useEventByCategory } from '../hooks/useEventContext';
import { CmsEvent } from '../models/EventResult';
import EventItem from '../components/EventItem';

export const EventPage = () => {
  const eventSlaStatus = useEventSlaStatus();

  return (
    <Box
      sx={{
        height: '100%',
      }}
    >
      {eventSlaStatus ? <AcceptedSlaEventPage /> : <EventSlaPage />}
    </Box>
  );
};

const AcceptedSlaEventPage = () => {
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const categories = useEventCategories();
  const navigate = useNavigate();
  const { categoryId = 'all-events' } = useParams<{ categoryId: string }>();
  const fetchEvents = useFetchEvents(categoryId);
  const bookmarkEvent = useBookmarkEvent();
  const { initial, events, error, loading } = useEventByCategory(categoryId);

  useIntersectionObserver(loadMoreRef, fetchEvents);

  const openCategory = useCallback(
    (index: number) => {
      navigate(
        index === 0
          ? Routes.resources.events.index
          : Routes.resources.events.categoryId(categories[index].databaseId),
      );
    },
    [navigate, categories],
  );

  const openEventDetails = useCallback(
    (event: CmsEvent) => {
      navigate(
        Routes.resources.events.postId(
          event.databaseId,
          categoryId ?? 'all-events',
        ),
      );
    },
    [categoryId, navigate],
  );

  return (
    <Box
      sx={{
        height: '100%',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pl: 7.5,
            pr: 7.5,
            pt: 10,
          }}
        >
          <Typography
            style={{
              fontSize: 20,
              fontWeight: 500,
              color: '#3b4559',
              cursor: 'pointer',
            }}
          >
            <FormattedMessage id='home.label.events' />
          </Typography>
        </Box>
        <ResourcesHeader
          categories={categories.map((eventCategory) => ({
            id: eventCategory.databaseId,
            name: eventCategory.name,
          }))}
          selectedCategoryId={Number(categoryId)}
          onCategoryClicked={openCategory}
        />
        <Divider
          sx={{
            ml: 7.5,
            mr: 7.5,
          }}
        />
      </Box>
      <Box
        className='hide-scrollbar'
        sx={{
          height: '100%',
          overflowY: 'auto',
        }}
      >
        <FunctionBuilder>
          {() => {
            if (error) {
              return <></>;
            } else if (!initial && events.length === 0) {
              return (
                <Box
                  sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    pt: 30,
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <img
                    src='/public/assets/images/dog_empty.svg'
                    width='60%'
                    alt='dog_empty'
                  />
                  <Typography
                    sx={{
                      pt: 10,
                    }}
                  >
                    <FormattedMessage id='resources.event.empty' />
                  </Typography>
                  <Button
                    variant='contained'
                    disableElevation
                    onClick={() => navigate(-1)}
                    sx={{
                      mt: 10,
                      borderRadius: 30,
                      py: 3.5,
                      width: '30%',
                    }}
                  >
                    <FormattedMessage id='common.go-back' />
                  </Button>
                </Box>
              );
            } else {
              return (
                <>
                  {events.length > 0 && (
                    <ResourcesCarousel>
                      {events.slice(0, 3).map((item) => (
                        <FeaturedResourcesItem
                          key={item.slug}
                          name={item.title}
                          imgUrl={getImageUrl(
                            item?.featuredImage?.node?.mediaDetails?.sizes,
                          )}
                          onItemClicked={() => openEventDetails(item)}
                        />
                      ))}
                    </ResourcesCarousel>
                  )}
                  <List
                    sx={{
                      pl: 7.5,
                      pr: 7.5,
                    }}
                  >
                    {events.map((item, index) => (
                      <EventItem
                        key={item.databaseId}
                        event={item}
                        ref={
                          index === (events ?? []).length - 1
                            ? loadMoreRef
                            : null
                        }
                        onBookmarkClicked={async (e) => {
                          e.stopPropagation();
                          await bookmarkEvent(item.databaseId, categoryId);
                        }}
                        onEventClicked={() => openEventDetails(item)}
                      />
                    ))}
                  </List>
                  <div style={{ height: '1px' }} ref={loadMoreRef} />
                  {loading && (
                    <LoadingIndicator
                      sx={{
                        height: initial ? '100%' : undefined,
                        py: 10,
                      }}
                    />
                  )}
                </>
              );
            }
          }}
        </FunctionBuilder>
      </Box>
    </Box>
  );
};
